import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Search, Eye, ShoppingBag, Banknote, QrCode } from 'lucide-react'
import { formatVND } from '@/lib/formatters'
import { useAdminOrdersStore } from '../../../state/ordersStore'
import type { OrderStatus } from '../../../model/types'
import { ORDER_STATUS_LABEL } from '../../../model/types'
import { StatusBadge } from '../../components/StatusBadge'
import { formatRelative } from '../../../lib/date-fmt'

type TabKey = 'all' | OrderStatus

const TABS: { key: TabKey; label: string }[] = [
  { key: 'all',       label: 'Tất cả' },
  { key: 'pending',   label: ORDER_STATUS_LABEL.pending },
  { key: 'confirmed', label: ORDER_STATUS_LABEL.confirmed },
  { key: 'shipping',  label: ORDER_STATUS_LABEL.shipping },
  { key: 'delivered', label: ORDER_STATUS_LABEL.delivered },
  { key: 'cancelled', label: ORDER_STATUS_LABEL.cancelled },
]

export function OrdersListPage() {
  const orders = useAdminOrdersStore((s) => s.orders)
  const loading = useAdminOrdersStore((s) => s.loading)
  const fetchAll = useAdminOrdersStore((s) => s.fetchAll)
  useEffect(() => { fetchAll() }, [fetchAll])
  const [tab, setTab] = useState<TabKey>('all')
  const [q, setQ] = useState('')

  const counts = useMemo(() => {
    const m: Record<TabKey, number> = { all: orders.length, pending: 0, confirmed: 0, shipping: 0, delivered: 0, cancelled: 0 }
    for (const o of orders) m[o.status]++
    return m
  }, [orders])

  const filtered = useMemo(() => {
    let items = orders.slice().sort((a, b) => b.createdAt.localeCompare(a.createdAt))
    if (tab !== 'all') items = items.filter((o) => o.status === tab)
    if (q.trim()) {
      const needle = q.trim().toLowerCase()
      items = items.filter((o) =>
        o.code.toLowerCase().includes(needle) ||
        o.address.phone.includes(needle) ||
        o.address.fullName.toLowerCase().includes(needle),
      )
    }
    return items
  }, [orders, tab, q])

  const totalRevenue = useMemo(
    () => orders.filter((o) => o.status === 'delivered').reduce((s, o) => s + o.grandTotal, 0),
    [orders],
  )

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-2xl font-bold text-text">Đơn hàng</h1>
        <p className="text-sm text-muted mt-1">
          {orders.length} đơn — doanh thu đã giao: <span className="font-semibold text-text">{formatVND(totalRevenue)}</span>
        </p>
      </div>

      <div className="bg-surface border border-border rounded-xl shadow-card p-4 space-y-4">
        <div className="flex items-center gap-2 overflow-x-auto pb-1">
          {TABS.map((t) => {
            const active = tab === t.key
            return (
              <button
                key={t.key}
                onClick={() => setTab(t.key)}
                className={[
                  'h-9 px-4 rounded-full text-sm font-medium inline-flex items-center gap-2 whitespace-nowrap transition',
                  active ? 'bg-brand-600 text-white' : 'bg-slate-100 text-text hover:bg-slate-200',
                ].join(' ')}
              >
                {t.label}
                <span className={[
                  'text-[11px] font-semibold px-1.5 py-0.5 rounded-full',
                  active ? 'bg-white/25 text-white' : 'bg-white text-muted',
                ].join(' ')}>
                  {counts[t.key]}
                </span>
              </button>
            )
          })}
        </div>

        <div className="relative max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Tìm mã đơn, SĐT, tên khách..."
            className="w-full h-10 rounded-md border border-border bg-surface pl-9 pr-3 text-sm outline-none focus:border-brand-500"
          />
        </div>
      </div>

      <div className="bg-surface border border-border rounded-xl shadow-card overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-muted text-xs uppercase">
              <tr>
                <th className="text-left px-4 py-3 font-medium">Mã đơn</th>
                <th className="text-left px-4 py-3 font-medium">Khách hàng</th>
                <th className="text-center px-4 py-3 font-medium">SP</th>
                <th className="text-right px-4 py-3 font-medium">Tổng</th>
                <th className="text-center px-4 py-3 font-medium">Thanh toán</th>
                <th className="text-left px-4 py-3 font-medium">Trạng thái</th>
                <th className="text-left px-4 py-3 font-medium">Thời gian</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="px-4 py-16 text-center text-muted">Đang tải...</td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-4 py-16 text-center">
                    <div className="w-16 h-16 mx-auto rounded-full bg-slate-100 flex items-center justify-center text-muted mb-3">
                      <ShoppingBag className="w-8 h-8" />
                    </div>
                    <div className="font-semibold text-text">Không có đơn hàng</div>
                    <div className="text-sm text-muted mt-1">Thử đổi tab hoặc xóa từ khóa tìm kiếm</div>
                  </td>
                </tr>
              )}
              {filtered.map((o) => (
                <tr key={o.id} className="border-t border-border hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono text-xs font-semibold text-brand-700">{o.code}</td>
                  <td className="px-4 py-3">
                    <div className="font-medium text-text">{o.address.fullName}</div>
                    <div className="text-xs text-muted">{o.address.phone}</div>
                  </td>
                  <td className="px-4 py-3 text-center text-muted">{o.items.reduce((s, it) => s + it.qty, 0)}</td>
                  <td className="px-4 py-3 text-right font-semibold text-text">{formatVND(o.grandTotal)}</td>
                  <td className="px-4 py-3">
                    <div className="inline-flex items-center gap-1 text-xs">
                      {o.payment === 'cod'
                        ? <><Banknote className="w-3.5 h-3.5 text-brand-600" /> COD</>
                        : <><QrCode className="w-3.5 h-3.5 text-brand-600" /> QR</>
                      }
                    </div>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={o.status} /></td>
                  <td className="px-4 py-3 text-muted text-xs whitespace-nowrap">{formatRelative(o.createdAt)}</td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/admin/don-hang/${o.id}`}
                      className="w-8 h-8 inline-flex items-center justify-center rounded-md hover:bg-brand-50 text-brand-600 transition"
                      aria-label="Xem chi tiết"
                    >
                      <Eye className="w-4 h-4" />
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  )
}
